import { useAppNavigation, useLocalStorageState } from '@faro/app-context';
import { FaroPersistentDrawer, FaroScrollFog, useScrollStateListener } from '@faro/design-system';
import { ComponentPropsWithRef, ReactNode, useState } from 'react';
import { useRoutes } from 'react-router';
import { ChatButton } from '../../package/intercom/ChatButton';
import { useSystemConfigQuery } from '../../query';
import sideNavWidgetRoutes from './routes';
import styles from './SideNav.module.scss';
import SideNavHeader, { LinkData } from './SideNavHeader/SideNavHeader';
import UserMenu, { MenuUser } from './UserMenu/UserMenu';

export interface SideNavProps extends ComponentPropsWithRef<'div'> {
    user: MenuUser;
    headerLinks?: LinkData[];
    headerClass?: string;
}

function SideNav(props: SideNavProps): ReactNode {
    const { children, headerLinks, headerClass, className = '', user, ref, ...rest } = props;

    const content = useAppNavigation();
    const routes = useRoutes(sideNavWidgetRoutes);
    const { data: systemConfig } = useSystemConfigQuery();

    const [open, setOpen] = useLocalStorageState('faro.side-nav.open', true);
    const [size, setSize] = useLocalStorageState<number | null>('faro.side-nav.size', null);

    const [scrollContainerElement, setScrollContainerElement] = useState<HTMLElement | null>();
    const [scrollElement, setScrollElement] = useState<HTMLElement | null>();

    useScrollStateListener(scrollElement, (scrollState: any) => {
        scrollContainerElement?.style.setProperty(
            `--faro-scroll-fog-opacity-top`,
            String(scrollState.canScrollUp ? 1 : 0)
        );
        scrollContainerElement?.style.setProperty(
            `--faro-scroll-fog-opacity-bottom`,
            String(scrollState.canScrollDown ? 1 : 0)
        );
    });

    return (
        <div {...rest} ref={ref} className={`${className} ${styles.sideNav}`}>
            <FaroPersistentDrawer
                resizable
                size={size || undefined}
                onSizeChange={value => {
                    setSize(value || null);
                }}
                snapOpenOrCloseWhenResizeUnder={250}
                className={styles.drawer}
                placement="left"
                elevation="lowered"
                open={open}
                onOpenChange={setOpen}
                data-automation-id="sideNav"
            >
                <div className={`${styles.sideNavContent} ${open ? styles.open : ''}`}>
                    <SideNavHeader className={headerClass} links={headerLinks} open={open} />
                    <div className={styles.main} ref={setScrollContainerElement}>
                        <div className={styles.scrollContainer} ref={setScrollElement}>
                            {routes}
                            {children}
                            {content}
                            <FaroScrollFog className={styles.scrollFog} />
                        </div>
                    </div>
                    <div className={styles.bottomArea}>
                        <ChatButton />
                        <UserMenu user={user} isExpanded helpCenterUrl={systemConfig?.helpUrl} />
                    </div>
                </div>
            </FaroPersistentDrawer>
        </div>
    );
}

export default SideNav;
